import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';
import { notifications } from '@mantine/notifications';

import { APPLY_BULK_ACTIONS_TO_PRICE_CHANGE_REQUESTS } from '../../../apollo/mutations/priceChangeRequest';
import type {
    BulkApplyPriceChangeRequestsInput,
    BulkApplyPriceChangeRequestsResult,
} from '../../../codegen/graphql';
import { showErrorNotification } from '../../../notifications';
import { QUICK_ACTION_PRICE_CHANGE_REFETCH_QUERIES } from '../pricing-batch-actions-cell/useApplyQuickAction';
import type { BatchQuickActionKind } from './BatchExpansionContext';
import type { QueuedPriceChangeQuickAction } from './pricingBatchStaging.types';

type QueueState = Map<string, QueuedPriceChangeQuickAction>;

type PricingBatchStagingContextValue = {
    readonly enqueue: (entry: QueuedPriceChangeQuickAction) => void;
    readonly removeFromQueue: (requestId: string) => void;
    readonly isRequestQueued: (requestId: string) => boolean;
    readonly getQueuedEntry: (
        requestId: string,
    ) => QueuedPriceChangeQuickAction | undefined;
    readonly queuedCount: number;
    readonly submitAllQueued: () => Promise<void>;
    readonly submitting: boolean;
    readonly clearQueue: () => void;
    readonly syncStagingWithQuickActionExpansionToggle: (args: {
        readonly requestId: string;
        readonly currentExpansionKind: BatchQuickActionKind | null;
        readonly clickedKind: BatchQuickActionKind;
    }) => void;
    readonly dequeueStagedQuickActionForPanelCancel: (
        requestId: string,
        panelKind: BatchQuickActionKind | null,
    ) => void;
    readonly hasOpenQuickActionExpansion: boolean;
    readonly setHasOpenQuickActionExpansion: (open: boolean) => void;
};

const PricingBatchStagingContext =
    createContext<PricingBatchStagingContextValue | null>(null);

export function PricingBatchStagingProvider({
    children,
}: Readonly<React.PropsWithChildren>) {
    const client = useApolloClient();

    const [queue, setQueue] = useState<QueueState>(() => new Map());
    const queueRef = useRef<QueueState>(queue);
    useEffect(() => {
        queueRef.current = queue;
    }, [queue]);

    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    const [hasOpenQuickActionExpansion, setHasOpenQuickActionExpansionState] =
        useState(false);

    const setHasOpenQuickActionExpansion = useCallback((open: boolean) => {
        setHasOpenQuickActionExpansionState(previous =>
            previous === open ? previous : open,
        );
    }, []);

    const enqueue = useCallback((entry: QueuedPriceChangeQuickAction) => {
        setQueue(previous => {
            const next = new Map(previous);
            next.set(entry.requestId, entry);
            return next;
        });
    }, []);

    const clearQueue = useCallback(() => {
        setQueue(new Map());
    }, []);

    const removeFromQueue = useCallback((requestId: string) => {
        setQueue(previous => {
            if (!previous.has(requestId)) {
                return previous;
            }
            const next = new Map(previous);
            next.delete(requestId);
            return next;
        });
    }, []);

    const syncStagingWithQuickActionExpansionToggle = useCallback(
        ({
            requestId,
            currentExpansionKind,
            clickedKind,
        }: {
            requestId: string;
            currentExpansionKind: BatchQuickActionKind | null;
            clickedKind: BatchQuickActionKind;
        }) => {
            const queued = queueRef.current.get(requestId);

            if (currentExpansionKind === null && queued) {
                if (queued.verb !== clickedKind) {
                    removeFromQueue(requestId);
                }
                return;
            }

            if (!queued) {
                return;
            }

            const switchingAway =
                currentExpansionKind !== null &&
                currentExpansionKind !== clickedKind;

            if (switchingAway && queued.verb === currentExpansionKind) {
                removeFromQueue(requestId);
            }
        },
        [removeFromQueue],
    );

    const dequeueStagedQuickActionForPanelCancel = useCallback(
        (requestId: string, panelKind: BatchQuickActionKind | null) => {
            if (!panelKind) {
                return;
            }
            const queued = queueRef.current.get(requestId);
            if (queued?.verb === panelKind) {
                removeFromQueue(requestId);
            }
        },
        [removeFromQueue],
    );

    const isRequestQueued = useCallback(
        (requestId: string) => queue.has(requestId),
        [queue],
    );

    const getQueuedEntry = useCallback(
        (requestId: string) => queue.get(requestId),
        [queue],
    );

    const queuedCount = queue.size;

    const submitAllQueued = useCallback(async () => {
        if (submittingRef.current) {
            return;
        }

        const snapshot = Array.from(queueRef.current.entries());
        if (snapshot.length === 0) {
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);

        const batchNotificationId = 'pricing-batch-staged-submit';

        try {
            notifications.show({
                id: batchNotificationId,
                loading: true,
                title: 'Submitting queued actions',
                message: `Processing ${snapshot.length} request${
                    snapshot.length === 1 ? '' : 's'
                }…`,
                autoClose: false,
                withCloseButton: false,
            });

            const input: BulkApplyPriceChangeRequestsInput = {
                items: snapshot.map(([, queued]) => ({
                    priceChangeRequestId: queued.requestId,
                    outcome: queued.outcome,
                    declinedReason: queued.metadata.reason ?? undefined,
                    note: queued.notes,
                    metadata: queued.metadata,
                })),
            };

            let payload: BulkApplyPriceChangeRequestsResult | null = null;

            try {
                const { data } = await client.mutate<{
                    readonly applyBulkActionsToPriceChangeRequests: BulkApplyPriceChangeRequestsResult;
                }>({
                    mutation: APPLY_BULK_ACTIONS_TO_PRICE_CHANGE_REQUESTS,
                    variables: { input },
                    refetchQueries: [
                        ...QUICK_ACTION_PRICE_CHANGE_REFETCH_QUERIES,
                    ],
                    awaitRefetchQueries: true,
                });

                payload = data?.applyBulkActionsToPriceChangeRequests ?? null;

                if (!payload) {
                    throw new Error('Empty bulk apply response');
                }

                const failedPcrs = new Set(
                    payload.failures.map(f => f.priceChangeRequestId),
                );
                snapshot.forEach(([requestId]) => {
                    if (!failedPcrs.has(requestId)) {
                        removeFromQueue(requestId);
                    }
                });
            } catch (maybeError: unknown) {
                const gqlMessage =
                    maybeError instanceof ApolloError
                        ? maybeError.graphQLErrors[0]?.message
                        : undefined;
                const fallback =
                    maybeError instanceof Error
                        ? maybeError.message
                        : undefined;

                notifications.update({
                    id: batchNotificationId,
                    loading: false,
                    autoClose: 6000,
                    withCloseButton: true,
                    color: 'red',
                    title: 'Submit failed',
                    message:
                        gqlMessage ??
                        fallback ??
                        'Could not submit queued actions. Try again shortly.',
                });
                showErrorNotification(
                    gqlMessage ??
                        fallback ??
                        'Queued actions could not be applied.',
                );
                return;
            }

            if (!payload) {
                return;
            }

            const { appliedCount, failures } = payload;
            const total = snapshot.length;
            const queuedById = new Map(
                snapshot.map(([, queued]) => [queued.requestId, queued]),
            );
            const failureSummary = failures
                .map(failure => {
                    const label =
                        queuedById.get(failure.priceChangeRequestId)
                            ?.requestLabel ?? 'Request';
                    return `${label}: ${failure.message}`;
                })
                .join('; ');

            const allApplied = failures.length === 0;
            const noneApplied = appliedCount === 0;

            let notificationColor: 'green' | 'orange' | 'red';
            let notificationTitle: string;
            let notificationMessage: string;

            if (allApplied) {
                notificationColor = 'green';
                notificationTitle = 'All queued actions applied';
                notificationMessage = `${appliedCount} of ${total} applied.`;
            } else if (noneApplied) {
                notificationColor = 'red';
                notificationTitle = 'Submit failed';
                notificationMessage = failureSummary;
            } else {
                notificationColor = 'orange';
                notificationTitle = 'Some queued actions failed';
                notificationMessage = `${appliedCount} of ${total} applied. Failed: ${failureSummary}`;
            }

            notifications.update({
                id: batchNotificationId,
                loading: false,
                autoClose: allApplied ? 5000 : 10000,
                withCloseButton: true,
                color: notificationColor,
                title: notificationTitle,
                message: notificationMessage,
            });

            if (noneApplied && total > 0) {
                showErrorNotification(failureSummary);
            }
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }, [client, removeFromQueue]);

    const value = useMemo<PricingBatchStagingContextValue>(
        () => ({
            enqueue,
            removeFromQueue,
            syncStagingWithQuickActionExpansionToggle,
            dequeueStagedQuickActionForPanelCancel,
            isRequestQueued,
            getQueuedEntry,
            queuedCount,
            submitAllQueued,
            submitting,
            clearQueue,
            hasOpenQuickActionExpansion,
            setHasOpenQuickActionExpansion,
        }),
        [
            enqueue,
            removeFromQueue,
            syncStagingWithQuickActionExpansionToggle,
            dequeueStagedQuickActionForPanelCancel,
            isRequestQueued,
            getQueuedEntry,
            queuedCount,
            submitAllQueued,
            submitting,
            clearQueue,
            hasOpenQuickActionExpansion,
            setHasOpenQuickActionExpansion,
        ],
    );

    return (
        <PricingBatchStagingContext.Provider value={value}>
            {children}
        </PricingBatchStagingContext.Provider>
    );
}

export function usePricingBatchStaging(): PricingBatchStagingContextValue {
    const ctx = useContext(PricingBatchStagingContext);
    if (!ctx) {
        throw new Error(
            'usePricingBatchStaging must be used within PricingBatchStagingProvider',
        );
    }
    return ctx;
}

export function useOptionalPricingBatchStaging(): PricingBatchStagingContextValue | null {
    return useContext(PricingBatchStagingContext);
}
